/**
 * Static placeholder for SearchTypeahead during SSR + first paint.
 *
 * Why this exists: SearchTypeahead pulls in `lib/search`, which pulls
 * in `lib/spec-code-index`, which pulls in ~85 KB of static spec data
 * across 5 files. Header is loaded on every page, so without this
 * split the search corpus rides every cold visit even when the user
 * never opens the search box.
 *
 * Header now React.lazy()'s SearchTypeahead and renders this skeleton
 * until the user signals intent. Same dimensions, same border-radius,
 * same padding — so layout doesn't shift when the real component
 * swaps in.
 *
 * The skeleton is a *fully functional* controlled input so the user
 * can start typing immediately. Each keystroke fires `onActivate`,
 * which Header uses to swap in the real SearchTypeahead with the
 * accumulated query as `initialQuery`. The Playwright e2e tests
 * (which `fill()` the input synchronously) keep working because the
 * skeleton accepts text instead of locking it out.
 *
 * Inline SVG for the magnifier so this module doesn't pull
 * `lucide-react/Search` back into the entry chunk.
 */

interface SearchTypeaheadSkeletonProps {
  className?: string;
  variant?: "desktop" | "mobile";
  /**
   * Current query text. Header owns the state so the value persists
   * when the skeleton unmounts and the real component takes over.
   */
  value?: string;
  /** Fires on every keystroke so Header can mirror the typed text. */
  onChange?: (value: string) => void;
  /**
   * Fires when the user signals intent to use search — focus, click,
   * pointer-down, keystroke, or touch. Header uses this to swap in
   * the real SearchTypeahead.
   */
  onActivate?: () => void;
  /**
   * Fires when the user submits the form (Enter or Search button).
   * Header navigates to /busca?q=… here so submission works even if
   * the lazy SearchTypeahead chunk hasn't arrived yet — important
   * for the e2e tests where fill() + press("Enter") fires before
   * the lazy import resolves.
   */
  onSubmit?: (value: string) => void;
}

const SearchIconSvg = ({ className }: { className: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const SearchTypeaheadSkeleton = ({
  className = "",
  variant = "desktop",
  value = "",
  onChange,
  onActivate,
  onSubmit,
}: SearchTypeaheadSkeletonProps) => {
  const inputClasses =
    variant === "desktop"
      ? "w-32 xl:w-48 pl-9 pr-3 py-2 bg-muted/50 border border-border rounded-full text-sm max-xl:placeholder:text-transparent cursor-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      : "w-full pl-10 pr-4 py-3 bg-surface-dark-elevated border border-surface-dark-hover rounded-xl text-base text-white placeholder:text-on-surface-dark-subtle cursor-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50";

  return (
    <div className={`relative ${className}`}>
      <form
        // action + method are the no-JS / pre-hydration fallback. If a
        // user (or the e2e suite) presses Enter before React has
        // attached its onSubmit handler — which can happen during
        // hydrateRoot's hydrate-then-recover path on slow devices —
        // the browser's default form submission goes to /busca?q=…
        // anyway. Once React hydrates, onSubmit takes over with the
        // SPA-friendly navigate() path. This makes the search
        // hydration-race-free.
        action="/busca"
        method="get"
        onSubmit={(e) => {
          e.preventDefault();
          // Read the input's current DOM value rather than the
          // closure-captured `value` prop. Reason: Playwright's
          // `fill() + press("Enter")` sequence on CI sometimes
          // races React reconciliation — by Enter time the DOM
          // <input>.value already reflects the typed text but the
          // skeleton's `value` prop closure may still hold the
          // pre-fill value because the parent's setState hasn't
          // flushed yet. Reading from FormData (which pulls from
          // input.value) is always current.
          const formData = new FormData(e.currentTarget);
          const q = (formData.get("q") as string) ?? value;
          onSubmit?.(q);
        }}
      >
        <SearchIconSvg
          className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
            variant === "mobile" ? "text-on-surface-dark-subtle" : "text-muted-foreground"
          }`}
        />
        {/*
          UNCONTROLLED INPUT (`defaultValue` only when non-empty;
          omitted entirely when empty). On purpose — React 18's
          `renderToString` emits `<input value>` (BARE attribute,
          no `=""`) for empty-string controlled OR `defaultValue=""`
          inputs. The browser parses bare `value` as `value=""`, but
          hydrateRoot reads the SSR HTML and decides the input is
          unhydratable, recovering by client-rendering the entire
          route subtree (#418/#422 cascade). Confirmed via
          MutationObserver capture during attempt 6: every page
          hydration replaced both search inputs (desktop + mobile)
          on first render, which cascaded recovery up to the route
          boundary.

          The conditional spread sidesteps it — when `value` is
          empty (the common case on first paint), no `value` /
          `defaultValue` attribute is emitted at all, so SSR HTML
          and hydration agree. When `value` is non-empty (e.g.
          Header restored `pendingQuery` from a previous typing
          burst), `defaultValue` sets the initial visible text;
          the browser then owns the input from that point.
          Keystrokes still fire `onChange` so Header's source-of-
          truth `pendingQuery` stays in sync — same UX, same
          form-submit fallback.
        */}
        <input
          type="text"
          name="q"
          placeholder="Buscar produtos, eventos…"
          aria-label="Buscar produtos e eventos"
          {...(value ? { defaultValue: value } : {})}
          onChange={(e) => {
            onChange?.(e.target.value);
            onActivate?.();
          }}
          onFocus={onActivate}
          onMouseEnter={onActivate}
          onPointerDown={onActivate}
          onTouchStart={onActivate}
          className={inputClasses}
        />
      </form>
    </div>
  );
};

export default SearchTypeaheadSkeleton;
